/**
 * The shape our APIs use for error bodies. Every field is `unknown` because the
 * body is unvalidated JSON: FastAPI's `detail` is a string for an HTTPException
 * but an array of issues for a validation failure, and only the upload
 * endpoints set `msg`. Read it with the accessors below rather than casting.
 */
type ApiErrorBody = {
  detail?: unknown
  msg?: unknown
}

const readStringField = (body: unknown, field: keyof ApiErrorBody): string | undefined => {
  const value = (body as ApiErrorBody | null | undefined)?.[field]
  return typeof value === 'string' ? value : undefined
}

const getMessage = (status: number | undefined, body: unknown) =>
  readStringField(body, 'detail') ?? `Request failed${status ? ` with status ${status}` : ''}`

/**
 * Normalises the raw error thrown by the generated fetch clients, which is the
 * parsed response body rather than an Error. Registered as an error interceptor
 * on each client in GeneratedApi, so consumers keep seeing a single error type.
 */
export class ApiError extends Error {
  readonly body: unknown
  readonly status: number | undefined
  readonly url: string | undefined

  constructor({
    body,
    status,
    url,
  }: {
    body: unknown
    status: number | undefined
    url: string | undefined
  }) {
    super(getMessage(status, body))
    this.name = 'ApiError'
    this.body = body
    this.status = status
    this.url = url
  }
}

export const DEFAULT_API_ERROR_MESSAGE = 'Something went wrong, please try again.'

/**
 * The message to show a user for a failed request. Prefers `msg` over `detail`
 * as the upload endpoints set both, with `msg` carrying the specific complaint.
 */
export const getApiErrorMessage = (
  error: ApiError,
  fallbackMessage: string = DEFAULT_API_ERROR_MESSAGE,
): string =>
  readStringField(error.body, 'msg') || readStringField(error.body, 'detail') || fallbackMessage
